import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchLatestScenarios, fetchOutputOverview } from './outputOverviewAPI';
import { RootState } from '../../state/store';
import { hideLoader } from '../ui/loaderSlice';
import { setScenarios } from '../outputSegmentDeepDive/outputSegmentDeepDiveSlice';
import { SCENARIO_OUTPUT_DATA } from '../../utils/dataConstants';
import { SelectMapperKeys, ViewByMapperKeys } from '../../types/common';

export interface OverviewState {
  filters: Record<string, any>;
  all_segment: {
    absolute: any[];
    incremental: any[];
    percentageChange: any[];
  };
  intervened_segment: {
    absolute: any[];
    incremental: any[];
    percentageChange: any[];
  };
}

export interface OutputOverviewState {
  data: any[] | null;
  latestScenarioNames: string[] | null;
  loading: boolean;
  isOutputScreen: boolean;
  error: string | null;
  overviewState: OverviewState;
}

const initialState: OutputOverviewState = {
  data: null,
  latestScenarioNames: null,
  loading: false,
  isOutputScreen: false,
  error: null,
  overviewState: {
    filters: {},
    all_segment: {
      absolute: [],
      incremental: [],
      percentageChange: [],
    },
    intervened_segment: {
      absolute: [],
      incremental: [],
      percentageChange: [],
    },
  },
};

const areFiltersEqual = (filter1: Record<string, any>, filter2: Record<string, any>): boolean => {
  const keys1 = Object.keys(filter1);
  const keys2 = Object.keys(filter2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;

    const value1 = filter1[key];
    const value2 = filter2[key];

    if (Array.isArray(value1) && Array.isArray(value2)) {
      if (value1.length !== value2.length || !value1.every((item, index) => item === value2[index])) {
        return false;
      }
    } else if (value1 !== value2) {
      return false;
    }
  }

  return true;
};

export const getOutputOverview = createAsyncThunk(
  'outputOverview/getOutputOverviewForComparison',
  async (
    { scenarioNames, option }: { scenarioNames?: string[]; option?: any } = {},
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const { selectedToggle, selectedViewBy, enabledKeys: filters } = state.outputFilters;

      const overviewState = state.outputOverview.overviewState;
      const segmentKey = selectedToggle === 'All Segment' ? 'all_segment' : 'intervened_segment';
      const viewByKey =
        selectedViewBy === 'Absolute'
          ? 'absolute'
          : selectedViewBy === 'Incremental'
          ? 'incremental'
          : 'percentageChange';

      const currentData =
        overviewState[segmentKey as keyof OverviewState][viewByKey as keyof OverviewState['all_segment']];

      if (areFiltersEqual(filters, overviewState.filters) && currentData.length > 0) {
        return {
          data: currentData,
          filters,
          selectedToggle,
          selectedViewBy,
        };
      } else {
        let payload: any = {
          select: SCENARIO_OUTPUT_DATA.SELECT_MAPPER[selectedToggle as SelectMapperKeys],
          view_by: SCENARIO_OUTPUT_DATA.VIEW_BY_MAPPER[selectedViewBy as ViewByMapperKeys],
          state: filters.state || [],
          bottler: filters.bottler || [],
        };

        if (scenarioNames?.length) {
          dispatch(setScenarios(scenarioNames));
          payload.scenarios = scenarioNames;
        } else {
          const latestScenarioNames: string[] = await fetchLatestScenarios();
          payload.scenarios = latestScenarioNames;
          dispatch(setScenarios(latestScenarioNames));
          dispatch(setLatestScenarioNames(latestScenarioNames));
        }

        const response = await fetchOutputOverview(payload);
        return {
          data: response,
          filters,
          selectedToggle,
          selectedViewBy,
        };
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error.message || 'Failed to fetch output overview for comparison';
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(hideLoader());
    }
  },
);

const outputOverviewSlice = createSlice({
  name: 'outputOverview',
  initialState,
  reducers: {
    resetOutputOverviewState: (state) => {
      state.data = null;
      state.error = null;
      state.loading = false;
    },
    removeScenario: (state, action: PayloadAction<string>) => {
      const scenarioNameToRemove = action.payload;
      if (state.data) {
        state.data = state.data.filter((scenario) => scenario.scenario_name !== scenarioNameToRemove);
      }

      const filterScenario = (array: any[]) => array.filter((item) => item.scenario_name !== scenarioNameToRemove);

      for (const segmentKey of ['all_segment', 'intervened_segment'] as const) {
        for (const viewByKey of ['absolute', 'incremental', 'percentageChange'] as const) {
          if (state.overviewState[segmentKey][viewByKey]?.length > 0) {
            state.overviewState[segmentKey][viewByKey] = filterScenario(state.overviewState[segmentKey][viewByKey]);
          }
        }
      }
    },
    setLatestScenarioNames: (state, action: PayloadAction<string[]>) => {
      state.latestScenarioNames = action.payload;
    },
    setOutputScreen: (state, action: PayloadAction<boolean>) => {
      state.isOutputScreen = action.payload;
    },
    resetOverviewData: (state) => {
      state.overviewState = initialState.overviewState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOutputOverview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getOutputOverview.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: any[];
          filters: any;
          selectedToggle: string;
          selectedViewBy: string;
        }>,
      ) => {
        state.loading = false;
        state.data = action.payload.data;
        const { data, filters, selectedToggle, selectedViewBy } = action.payload;

        const segmentKey = selectedToggle === 'All Segment' ? 'all_segment' : 'intervened_segment';
        const viewByKey =
          selectedViewBy === 'Absolute'
            ? 'absolute'
            : selectedViewBy === 'Incremental'
            ? 'incremental'
            : 'percentageChange';

        state.overviewState.filters = filters;
        state.overviewState[segmentKey][viewByKey] = data;
      },
    );
    builder.addCase(getOutputOverview.rejected, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { resetOutputOverviewState, removeScenario, setLatestScenarioNames, setOutputScreen, resetOverviewData } =
  outputOverviewSlice.actions;

export const selectOutputOverviewData = (state: RootState) => state.outputOverview.data;
export const selectOutputOverviewLoading = (state: RootState) => state.outputOverview.loading;
export const selectOutputOverviewError = (state: RootState) => state.outputOverview.error;

export default outputOverviewSlice.reducer;
